import { AppState } from '@redux/store'
import { AuthToken } from '@typesApp/Session'
import { Logger } from '@utils/logger'

export const KEY_AUTH_TOKEN = 'direct-billing.auth-token'
const FETCHING_STATUS = 'FETCHING'
const EMPTY_TOKEN: AuthToken = {
  accessToken: '',
  sessionId: '',
  customerNumber: '',
  customerEmail: '',
}

export const hasCognitoSession = (token?: AuthToken): boolean =>
  token !== undefined && Object.keys(token).length > 0 && Boolean(token?.accessToken)

interface FetchResult {
  error?: {
    status: number | string
  }
}

export const isAuthenticationError = (result: FetchResult): boolean => {
  return Boolean(result.error) && result.error?.status === 401
}

export const isSessionValid = (state: unknown): boolean => {
  const appState = state as AppState
  const sessionId = appState.connector.session?.sessionId
  if (!sessionId) {
    return false
  }
  return !appState.connector.sessionExpired && !appState.connector.isLoggingOut
}

export const hasLocalAuthToken = (sessionId: string, customerNumber?: string): boolean => {
  if (!sessionId && !customerNumber) {
    return false
  }

  const token = getLocalAuthToken()
  if (!hasCognitoSession(token)) {
    return false
  }

  if (sessionId) return token.sessionId === sessionId
  return token.customerNumber === customerNumber
}

export const getLocalAuthToken = (): AuthToken => {
  const value = window.sessionStorage.getItem(KEY_AUTH_TOKEN)
  if (value === FETCHING_STATUS) {
    return EMPTY_TOKEN
  }

  try {
    return JSON.parse(value ?? '{}')
  } catch (e) {
    Logger.warn('Unable to get local auth token', e)
    return EMPTY_TOKEN
  }
}

export const setLocalAuthTokenFetching = () => {
  window.sessionStorage.setItem(KEY_AUTH_TOKEN, FETCHING_STATUS)
}

export const isLocalAuthTokenFetching = () => {
  return window.sessionStorage.getItem(KEY_AUTH_TOKEN) === FETCHING_STATUS
}

export const setLocalAuthToken = (token: AuthToken) => {
  const { claimRecordId, customerEmail, ...rest } = token
  window.sessionStorage.setItem(KEY_AUTH_TOKEN, JSON.stringify(rest))
}

export const clearLocalAuthToken = () => {
  window.sessionStorage.removeItem(KEY_AUTH_TOKEN)
}
